// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridItemProps } from "@insite/mobius/GridItem";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Notify from "@synd/App/Components/Notify/Notify";
import { QtyFieldStyles } from "@synd/App/Components/Qty/QtyField";
import { CustomAppState } from "@synd/App/Store/Reducers";
import { xIsAuthenticated } from "@synd/App/Store/Selectors/GeneralSelectors";
import { getSettingsCollection } from "@synd/App/Store/Selectors/SettingsSelectors";
import Button from "@synd/MobiusOverrides/Button/Button";
import { SelectPresentationProps } from "@synd/MobiusOverrides/Select";
import ProductAddToCartButton from "@synd/Overrides/Components/ProductAddToCartButton";
import ProductQuantityOrdered from "@synd/Overrides/Components/ProductQuantityOrdered";
import { mergeWithCss } from "@synd/XDL/Utilities/mergeWithCss";
import * as React from "react";
import { FC, useState } from "react";
import { shallowEqual } from "react-redux";

export interface ProductActionsProps {
    carouselId?: string;
    showActions?: boolean;
    alwaysShowQty?: boolean;
    extendedStyles?: ProductActionsStyle;
    onAddToCartClick?: () => void;
    currentPage?: string;
    disableUOM?: boolean;
    hideUOM?: boolean;
    showQtyActions?: boolean;
    showMultipleSaleQty?: boolean;
    isPDPActions?: boolean;
    quantityFieldVariant?: "input" | "select";
    showQtyLabel?: boolean;
}

type Props = ProductActionsProps & HasProductContext;

export interface ProductActionsStyle {
    wrapper?: InjectableCss;
    qtyWrapper?: GridItemProps;
    addToCartButtonWrapper?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    uomSelectWrapper?: GridItemProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    multipleSaleQty?: InjectableCss;
    viewButton?: ButtonPresentationProps;
    productQuantityOrdered?: QtyFieldStyles;
    buttonsWrapper?: InjectableCss;
    signInButton?: ButtonPresentationProps;
    registerButton?: ButtonPresentationProps;
    notify?: InjectableCss;
}

export const productActionsStyle: ProductActionsStyle = {};

const ProductActions: FC<Props> = ({
    productContext,
    carouselId,
    extendedStyles,
    onAddToCartClick,
    hideUOM,
    quantityFieldVariant,
    isPDPActions,
    showQtyLabel,
}) => {
    const [styles] = useState(() => mergeWithCss(productActionsStyle, extendedStyles));
    const uomRef = React.useRef<HTMLDivElement>(null);

    const {
        productSettings,
        isAuthenticated,
        signInPageUrl,
        signUpPageUrl,
        dipatchQtyAdjustedNotify,
        configurationSelection,
        configurationCompleted,
        variantSelectionCompleted,
    } = useAppSelector(state => {
        const currentLocation = getLocation(state);

        const returnUrl = encodeURIComponent(`${currentLocation.pathname}${currentLocation.search}`);

        const signInPageUrl = (getPageLinkByPageType(state, "SignInPage")?.url || "") + `?returnUrl=${returnUrl}`;
        // TODO: Confirm if its OOTB registration page or a new custom page
        const signUpPageUrl =
            (getPageLinkByPageType(state, "CreateAccountPage")?.url || "") + `?returnUrl=${returnUrl}`;

        return {
            productSettings: getSettingsCollection(state).productSettings,
            isAuthenticated: xIsAuthenticated(state),
            signInPageUrl,
            signUpPageUrl,
            dipatchQtyAdjustedNotify: (state as CustomAppState).xProductDetails.displayQtyAdjustedNotify,
            configurationSelection: state.pages.productDetails.configurationSelection,
            configurationCompleted: state.pages.productDetails.configurationCompleted,
            variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
        };
    }, shallowEqual);

    const unitOfMeasures = productContext.product.unitOfMeasures;
    const willShowUnitOfMeasureSelect =
        !hideUOM &&
        !(
            !productSettings?.alternateUnitsOfMeasure ||
            !unitOfMeasures ||
            unitOfMeasures.filter(uom => uom.unitOfMeasure).length <= 1
        );

    // <XNG-Change>
    const history = useHistory();
    const viewProduct = () => {
        history.push(productContext.productInfo.productDetailPath);
    };

    const onSignInHandler = () => {
        history.push(signInPageUrl);
    };

    const onRegisterHandler = () => {
        history.push(signUpPageUrl);
    };

    if (!isAuthenticated && isPDPActions) {
        return (
            <StyledWrapper {...styles.buttonsWrapper}>
                <Button onClick={onSignInHandler} data-test-selector="signin_button" {...styles.signInButton}>
                    {translate("Sign In")}
                </Button>
                <Button onClick={onRegisterHandler} data-test-selector="register_button" {...styles.registerButton}>
                    {translate("Register Now")}
                </Button>
            </StyledWrapper>
        );
    }

    if (!isAuthenticated || (productContext.product.cantBuy && !isPDPActions)) {
        return (
            <Button onClick={viewProduct} data-test-selector="view_button" {...styles.viewButton}>
                {translate("View Product")}
            </Button>
        );
    }
    // XNG-Changes: Show QTY input for variant parent
    if (productContext.product.isVariantParent && !isPDPActions) {
        return (
            <StyledWrapper {...styles.wrapper} data-test-selector="product_actions">
                <StyledWrapper {...styles.qtyWrapper}>
                    <VisuallyHidden as="label" htmlFor={`${carouselId}-qty`} id={`${carouselId}-qty-label`}>
                        {translate("QTY")}
                    </VisuallyHidden>
                    <ProductQuantityOrdered
                        labelOverride=""
                        data-test-selector="productQty"
                        variant={quantityFieldVariant}
                        disabled
                        extendedStyles={styles.productQuantityOrdered}
                    />
                </StyledWrapper>
                <Button onClick={viewProduct} data-test-selector="view_button" {...styles.viewButton}>
                    {translate("View Options")}
                </Button>
            </StyledWrapper>
        );
    }
    // </XNG-Change>
    return (
        <>
            {dipatchQtyAdjustedNotify && isPDPActions && (
                <Notify type="warning" {...styles.notify}>
                    {siteMessage("Custom_PDP_Qty_Adjusted")}
                </Notify>
            )}
            <StyledWrapper
                {...styles.wrapper}
                data-test-selector="product_actions"
                {...{ hasUOM: willShowUnitOfMeasureSelect }}
            >
                <StyledWrapper {...styles.qtyWrapper}>
                    <VisuallyHidden as="label" htmlFor={`${carouselId}-qty`} id={`${carouselId}-qty-label`}>
                        {translate("QTY")}
                    </VisuallyHidden>
                    <ProductQuantityOrdered
                        labelOverride=""
                        data-test-selector="productQty"
                        variant={quantityFieldVariant}
                        showLabel={showQtyLabel}
                        extendedStyles={styles.productQuantityOrdered}
                        {...(isPDPActions
                            ? {
                                  configurationSelection,
                                  configurationCompleted,
                                  variantSelectionCompleted,
                              }
                            : {})}
                    />
                </StyledWrapper>

                {willShowUnitOfMeasureSelect && (
                    <StyledWrapper {...styles.uomSelectWrapper} ref={uomRef}>
                        <ProductUnitOfMeasureSelect extendedStyles={styles.unitOfMeasureSelect} />
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.addToCartButtonWrapper} {...{ hasUom: willShowUnitOfMeasureSelect }}>
                    <ProductAddToCartButton
                        onAddToCartClick={onAddToCartClick}
                        extendedStyles={styles.productAddToCartButtonStyles}
                        data-test-selector="addToCartBtn"
                        {...{ isPDPActions }}
                        {...(isPDPActions && { configurationCompleted, variantSelectionCompleted })}
                    />
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

export default withProductContext(ProductActions);
